'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import type { Terminal } from '@xterm/xterm';
import type { FitAddon } from '@xterm/addon-fit';

interface TerminalComponentProps {
  wsUrl: string;
  command?: string;
  onExit?: (exitCode: number | null) => void;
  onError?: (error: string) => void;
  /**
   * Optional handle for the page to type into the PTY as if at the keyboard.
   * Set once the socket is open, cleared on teardown. The brief session uses
   * it to send `/exit` when the unattended run has finished — Claude Code's
   * REPL waits for input forever otherwise, so the PTY would never exit.
   */
  inputRef?: React.MutableRefObject<((data: string) => void) | null>;
}

const darkTheme = {
  background: '#1a1b26',
  foreground: '#a9b1d6',
  cursor: '#c0caf5',
  cursorAccent: '#1a1b26',
  selectionBackground: '#33467c',
  black: '#32344a',
  red: '#f7768e',
  green: '#9ece6a',
  yellow: '#e0af68',
  blue: '#7aa2f7',
  magenta: '#ad8ee6',
  cyan: '#449dab',
  white: '#787c99',
  brightBlack: '#444b6a',
  brightRed: '#ff7a93',
  brightGreen: '#b9f27c',
  brightYellow: '#ff9e64',
  brightBlue: '#7da6ff',
  brightMagenta: '#bb9af7',
  brightCyan: '#0db9d7',
  brightWhite: '#acb0d0',
};

const lightTheme = {
  background: '#fafafa',
  foreground: '#3760bf',
  cursor: '#3760bf',
  cursorAccent: '#fafafa',
  selectionBackground: '#b6d1fc',
  black: '#e9e9ed',
  red: '#f52a65',
  green: '#587539',
  yellow: '#8c6c3e',
  blue: '#2e7de9',
  magenta: '#9854f1',
  cyan: '#007197',
  white: '#6172b0',
  brightBlack: '#a1a6c5',
  brightRed: '#f52a65',
  brightGreen: '#587539',
  brightYellow: '#8c6c3e',
  brightBlue: '#2e7de9',
  brightMagenta: '#9854f1',
  brightCyan: '#007197',
  brightWhite: '#3760bf',
};

function getTerminalTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? darkTheme : lightTheme;
}

function isMacPlatform() {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/**
 * Copy `text` to the *browser's* clipboard — the machine viewing the page, not the
 * host running the PTY.
 *
 * navigator.clipboard only exists in a secure context, so it is present on
 * https://minime.oddaddress.com and on http://localhost:3000, and absent when the
 * page is reached over plain HTTP by IP or LAN hostname. The execCommand path is
 * the fallback for that case: deprecated, but it is the only thing that works on a
 * non-secure origin and it is still supported everywhere. Both paths must run
 * inside the user gesture that triggered them, so this stays synchronous up to the
 * point of the async write.
 */
async function writeToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Rejected — usually a permissions policy or an unfocused document. Fall
      // through to execCommand rather than giving up.
    }
  }

  const scratch = document.createElement('textarea');
  scratch.value = text;
  // Keep it out of view without using display:none or visibility:hidden, either of
  // which makes the selection uncopyable.
  scratch.setAttribute('readonly', '');
  scratch.style.position = 'fixed';
  scratch.style.top = '-1000px';
  scratch.style.opacity = '0';
  document.body.appendChild(scratch);

  const previouslyFocused = document.activeElement as HTMLElement | null;
  try {
    scratch.select();
    scratch.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(scratch);
    // xterm loses keyboard input if focus is left on a detached element.
    previouslyFocused?.focus?.();
  }
}

export function TerminalComponent({
  wsUrl,
  command = '/reflect',
  onExit,
  onError,
  inputRef
}: TerminalComponentProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstanceRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const [status, setStatus] = useState<'connecting' | 'connected' | 'started' | 'exited' | 'error'>('connecting');
  const [isClient, setIsClient] = useState(false);
  const [clipboardNotice, setClipboardNotice] = useState<string | null>(null);
  const noticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Report clipboard outcomes in the DOM rather than with term.write(). Claude Code
  // draws a full-screen TUI and repaints constantly, so anything written into the
  // terminal buffer is scribbled over its output and wiped by the next redraw.
  //
  // This exists because every failure on the copy path used to be silent: an empty
  // selection did nothing, and a rejected clipboard write was swallowed by an empty
  // catch. "Nothing happened" gives no way to tell which of the two broke, which is
  // how a copy path stayed broken across several fixes.
  const showClipboardNotice = useCallback((message: string) => {
    setClipboardNotice(message);
    if (noticeTimeoutRef.current) clearTimeout(noticeTimeoutRef.current);
    noticeTimeoutRef.current = setTimeout(() => setClipboardNotice(null), 4000);
  }, []);

  useEffect(() => {
    return () => {
      if (noticeTimeoutRef.current) clearTimeout(noticeTimeoutRef.current);
    };
  }, []);

  // Set isClient on mount to avoid hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Handle window resize. This only refits; the PTY is told the new size by the
  // term.onResize handler below. Reporting proposeDimensions() here instead was a
  // bug: it's a prediction of what would fit, not what xterm actually settled on,
  // and it was sent even when the two disagreed.
  const handleResize = useCallback(() => {
    if (fitAddonRef.current && terminalInstanceRef.current) {
      fitAddonRef.current.fit();
    }
  }, []);

  // Read text straight out of xterm's buffer. This is the path that does not depend on
  // a selection at all, which matters because Claude Code owns the mouse: a plain drag
  // never produces an xterm selection to copy.
  //
  // Takes everything available, which is what "copy all" can mean here.
  //
  // Where xterm has scrollback (the plain shell, before Claude Code starts) that is the
  // whole buffer including scrolled-off lines. Inside Claude Code it is the visible frame
  // and no more: Claude Code runs in the alternate screen buffer, where xterm keeps no
  // scrollback at all (buffer.hasScrollback is false and buf.length === term.rows), and
  // the transcript above the fold lives inside Claude Code rather than here. So "all" and
  // "screen" are the same operation in the TUI — which is why there is one button, not
  // two. buf.length > term.rows is the public-API way to detect scrollback;
  // hasScrollback itself is internal.
  const readAllAvailable = useCallback(() => {
    const term = terminalInstanceRef.current;
    if (!term) return '';
    const buf = term.buffer.active;
    const hasScrollback = buf.length > term.rows;
    const first = hasScrollback ? 0 : buf.viewportY;
    const last = hasScrollback ? buf.length - 1 : buf.viewportY + term.rows - 1;
    const lines: string[] = [];
    for (let i = first; i <= last; i++) {
      lines.push(buf.getLine(i)?.translateToString(true) ?? '');
    }
    // Drop leading/trailing blank lines; keep interior ones so layout survives.
    while (lines.length && !lines[0].trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    return lines.join('\n');
  }, []);

  // Selection first, visible frame as the fallback. Runs inside the click or keypress
  // that triggered it, which is what keeps Safari's clipboard gate happy.
  const copySelectionOrScreen = useCallback(() => {
    const selection = terminalInstanceRef.current?.getSelection();
    const usingSelection = Boolean(selection);
    const text = selection || readAllAvailable();
    if (!text) {
      showClipboardNotice('Nothing to copy');
      return;
    }
    void writeToClipboard(text).then((ok) => {
      if (!ok) {
        showClipboardNotice('Copy failed \u2014 the browser blocked the clipboard write');
        return;
      }
      showClipboardNotice(
        usingSelection
          ? `Copied selection (${text.length} chars) to this browser`
          : `Copied all ${text.split('\n').length} lines to this browser \u2014 Option+drag to copy just a selection`
      );
    });
  }, [readAllAvailable, showClipboardNotice]);

  useEffect(() => {
    if (!terminalRef.current || !isClient) return;

    let term: Terminal | null = null;
    let ws: WebSocket | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let mounted = true;
    let mediaQueryCleanup: (() => void) | null = null;
    let fitTimeoutId: ReturnType<typeof setTimeout> | null = null;

    // Dynamically import xterm modules (browser-only)
    const initTerminal = async () => {
      const [xtermModule, fitAddonModule, webLinksModule] = await Promise.all([
        import('@xterm/xterm'),
        import('@xterm/addon-fit'),
        import('@xterm/addon-web-links'),
      ]);

      // Import CSS dynamically
      // @ts-expect-error - CSS imports are handled by Next.js bundler
      await import('@xterm/xterm/css/xterm.css');

      if (!mounted || !terminalRef.current) return;

      const { Terminal } = xtermModule;
      const { FitAddon } = fitAddonModule;
      const { WebLinksAddon } = webLinksModule;

      const initialTheme = getTerminalTheme();

      // Initialize terminal
      term = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: 'Menlo, Monaco, "Courier New", monospace',
        theme: initialTheme,
        allowTransparency: true,
        scrollback: 10000,
        // Claude Code turns on mouse tracking (?1000h/?1002h/?1003h/?1006h), so xterm
        // routes every drag to the app and a drag never becomes a text selection —
        // which left the Cmd+C handler below with nothing to copy. xterm only lets a
        // modifier override mouse mode when this is set: on macOS shouldForceSelection
        // is `altKey && macOptionClickForcesSelection`, so without it Option+drag does
        // nothing either and selection is simply impossible. Other platforms use Shift
        // and need no option. Only affects the mouse; Option as a key modifier is
        // governed by macOptionIsMeta and is untouched.
        macOptionClickForcesSelection: true,
      });

      const fitAddon = new FitAddon();
      const webLinksAddon = new WebLinksAddon();

      term.loadAddon(fitAddon);
      term.loadAddon(webLinksAddon);
      term.open(terminalRef.current);

      terminalInstanceRef.current = term;
      fitAddonRef.current = fitAddon;

      // Component unmounted during async init — clean up and bail
      if (!mounted) {
        term.dispose();
        terminalInstanceRef.current = null;
        fitAddonRef.current = null;
        return;
      }

      // Fit terminal to container
      fitTimeoutId = setTimeout(() => {
        if (mounted) fitAddon.fit();
      }, 0);

      // Connect to WebSocket
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        // Refit before reporting so xterm has settled on its final geometry, then
        // report what it actually displays rather than proposeDimensions()' guess.
        // If the PTY is told more rows than xterm renders, the shell draws its
        // bottom UI below the visible viewport — which is how Claude's status line
        // ended up hanging off the bottom of the terminal.
        fitAddon.fit();
        ws!.send(JSON.stringify({
          type: 'start',
          command,
          cols: term!.cols,
          rows: term!.rows
        }));
        if (inputRef) {
          inputRef.current = (data: string) => {
            if (ws?.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'input', data }));
            }
          };
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          switch (msg.type) {
            case 'output':
              term?.write(msg.data);
              break;
            case 'started':
              setStatus('started');
              break;
            case 'exit':
              setStatus('exited');
              onExit?.(msg.exitCode);
              break;
            case 'error':
              setStatus('error');
              term?.write(`\r\n\x1b[31mError: ${msg.data}\x1b[0m\r\n`);
              onError?.(msg.data);
              break;
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = () => {
        setStatus('error');
        term?.write('\r\n\x1b[31mConnection error. Is the terminal server running?\x1b[0m\r\n');
        onError?.('WebSocket connection failed');
      };

      ws.onclose = () => {
        // Only show message if not already exited or errored
        term?.write('\r\n\x1b[33mConnection closed.\x1b[0m\r\n');
      };

      // Clipboard shortcuts. xterm turns most key chords into terminal input, so
      // these have to be intercepted. Returning false stops xterm from sending the
      // keystroke to the shell but does not preventDefault, so the browser's own
      // paste event still reaches xterm's textarea and is forwarded as input.
      const isMac = isMacPlatform();
      term.attachCustomKeyEventHandler((event) => {
        if (event.type !== 'keydown') return true;

        // Cmd+C/Cmd+V on macOS, Ctrl+Shift+C/Ctrl+Shift+V elsewhere — a bare
        // Ctrl+C has to stay available as SIGINT.
        const isClipboardChord = isMac
          ? event.metaKey && !event.ctrlKey && !event.altKey
          : event.ctrlKey && event.shiftKey && !event.altKey && !event.metaKey;
        if (!isClipboardChord) return true;

        const key = event.key.toLowerCase();

        if (key === 'c') {
          const selection = terminalInstanceRef.current?.getSelection();
          if (!selection) {
            // Claude Code owns the mouse, so a PLAIN drag is its selection, not
            // xterm's, and is invisible here. Option+drag forces xterm's own selection
            // (macOptionClickForcesSelection above), which is what this can read.
            showClipboardNotice('Nothing selected — Option+drag to select, or use Copy');
            return false;
          }
          const chars = selection.length;
          void writeToClipboard(selection).then((ok) => {
            showClipboardNotice(
              ok
                ? `Copied ${chars} character${chars === 1 ? '' : 's'} to this browser`
                : 'Copy failed \u2014 the browser blocked the clipboard write'
            );
          });
          return false;
        }

        if (key === 'v') {
          return false;
        }

        return true;
      });

      // xterm is the single source of truth for its own size: whenever it settles on
      // a new row/column count, tell the PTY. The container-resize path can't carry
      // this on its own — ResizeObserver's first callback fires before the socket is
      // open, so that resize was dropped and nothing re-sent it, leaving the PTY on
      // a stale row count for the whole session if the window was never resized.
      term.onResize(({ cols, rows }) => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'resize', cols, rows }));
        }
      });

      // Handle terminal input
      term.onData((data) => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'input', data }));
        }
      });

      // Handle resize
      window.addEventListener('resize', handleResize);

      // ResizeObserver for container size changes
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      if (terminalRef.current) {
        resizeObserver.observe(terminalRef.current);
      }

      // Listen for OS theme changes and update terminal theme
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleThemeChange = () => {
        if (term) {
          term.options.theme = getTerminalTheme();
        }
      };
      mediaQuery.addEventListener('change', handleThemeChange);
      mediaQueryCleanup = () => mediaQuery.removeEventListener('change', handleThemeChange);
    };

    initTerminal();

    // Cleanup
    return () => {
      mounted = false;
      if (fitTimeoutId) clearTimeout(fitTimeoutId);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      mediaQueryCleanup?.();

      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'kill' }));
        ws.close();
      }

      term?.dispose();
      terminalInstanceRef.current = null;
      fitAddonRef.current = null;
      wsRef.current = null;
      if (inputRef) inputRef.current = null;
    };
  }, [wsUrl, command, handleResize, onExit, onError, isClient, showClipboardNotice, inputRef]);

  return (
    <div className="relative h-full w-full">
      {status === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#fafafa] text-gray-500 dark:bg-[#1a1b26] dark:text-gray-400">
          Provisioning backend agent resources...
        </div>
      )}
      {/* FitAddon derives the row count from this element's content box, so its
          padding directly costs terminal rows — keep it tight. */}
      <div
        ref={terminalRef}
        className="h-full w-full bg-[#fafafa] dark:bg-[#1a1b26]"
        style={{ padding: '4px' }}
      />
      {/* Copy affordances. Claude Code has the mouse, so a plain drag is its selection
          and never reaches the browser; these read xterm's selection when Option+drag
          made one, and the buffer otherwise. Absolute so the tuned terminal height is
          untouched. */}
      {/* Opacity lives on the button, not the wrapper: on the wrapper, hovering one
          control lit up every control in it. */}
      <div className="absolute right-2 top-2 z-10 flex gap-1">
        <button
          type="button"
          onClick={copySelectionOrScreen}
          title="Copies the Option+drag selection if there is one, otherwise everything available. Inside Claude Code that is the visible screen — transcript scrolled above the fold lives in Claude Code, not the browser, so nothing can reach it."
          className="rounded bg-zinc-900/80 px-2 py-1 text-xs text-zinc-100 opacity-40 transition-opacity hover:bg-zinc-900 hover:opacity-100 dark:bg-zinc-100/85 dark:text-zinc-900 dark:hover:bg-zinc-100"
        >
          Copy
        </button>
      </div>
      {/* Absolutely positioned on purpose: the terminal's height is tuned and an
          in-flow element here would change the row count. */}
      {clipboardNotice && (
        <div className="pointer-events-none absolute bottom-2 right-2 z-10 rounded bg-zinc-900/85 px-2 py-1 text-xs text-zinc-100 shadow dark:bg-zinc-100/90 dark:text-zinc-900">
          {clipboardNotice}
        </div>
      )}
    </div>
  );
}
